import { translateError } from "@/lib/errors";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Loader2, Music, Plus, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/primitives/Button";
import { Card } from "@/components/primitives/Card";
import {
  addWatermark, deleteMusic, deleteWatermarkById, fmtDuration, musicAudioUrl, patchMusic, patchWatermarkById,
  uploadMusic, watermarkImageUrlById,
  type Montage, type MontageMusic, type MontageWatermark, type WatermarkPosition,
} from "@/lib/montageApi";

const errMsg = (err: any, fallback: string) => translateError(err?.response?.data?.detail) || fallback;

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  disabled?: boolean;
  testid: string;
  onChange: (v: number) => void;
}

function SliderRow({ label, value, min, max, step = 1, suffix = "%", disabled, testid, onChange }: SliderProps) {
  return (
    <label className="block space-y-1.5">
      <span className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="uppercase tracking-widest">{label}</span>
        <span data-testid={`${testid}-value`} className="font-mono text-foreground">{value}{suffix}</span>
      </span>
      <input
        type="range"
        data-testid={testid}
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/10 accent-[#2E7BFF] disabled:cursor-not-allowed disabled:opacity-40"
      />
    </label>
  );
}

function useDebounced<T extends object>(save: (patch: T) => void, delay = 450) {
  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (t.current) clearTimeout(t.current); }, []);
  return (patch: T) => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => save(patch), delay);
  };
}

const POSITIONS: { v: WatermarkPosition; label: string }[] = [
  { v: "top-left", label: "watermark.topLeft" },
  { v: "top-right", label: "watermark.topRight" },
  { v: "bottom-left", label: "watermark.bottomLeft" },
  { v: "bottom-right", label: "watermark.bottomRight" },
  { v: "center", label: "watermark.center" },
];

interface PanelProps {
  m: Montage;
  onUpdate: (m: Montage) => void;
  disabled: boolean;
}

function WatermarkRow({ m, wm, index, onUpdate, disabled }: PanelProps & { wm: MontageWatermark; index: number }) {
  const { t } = useTranslation();
  const [local, setLocal] = useState<MontageWatermark>(wm);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setLocal(wm); }, [wm.updated_at, wm.id]);

  const commit = useDebounced<Partial<MontageWatermark>>(async (patch) => {
    try {
      onUpdate(await patchWatermarkById(m.id, wm.id, patch as any));
    } catch (err) {
      toast.error(errMsg(err, t("watermark.notSaved")));
    }
  });
  const set = (patch: Partial<MontageWatermark>) => {
    setLocal((prev) => ({ ...prev, ...patch }));
    commit(patch);
  };
  const remove = async () => {
    if (!window.confirm(`Rimuovere il watermark ${index + 1}?`)) return;
    setBusy(true);
    try {
      onUpdate(await deleteWatermarkById(m.id, wm.id));
      toast.success(t("watermark.removed"));
    } catch (err) {
      toast.error(errMsg(err, t("watermark.removeFailed")));
    } finally {
      setBusy(false);
    }
  };
  const off = disabled || busy;
  const tid = (k: string) => `montage-watermark-${index}-${k}`;

  return (
    <div data-testid={`montage-watermark-card-${index}`} className="glass-panel space-y-3 rounded-xl p-3">
      <div className="flex items-center gap-3">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md border border-[#2E7BFF]/50 bg-[#2E7BFF]/15 font-display text-xs font-bold text-[#7FD4FF]">{index + 1}</span>
        <div className="grid h-12 w-16 shrink-0 place-items-center overflow-hidden rounded-md border border-[#2E7BFF]/30 bg-[#05070D]">
          <img data-testid={tid("preview")} src={watermarkImageUrlById(m.id, wm.id, wm.updated_at)} alt="watermark" className="max-h-full max-w-full object-contain" />
        </div>
        <div className="min-w-0 flex-1 text-xs">
          <p data-testid={tid("filename")} className="truncate text-foreground">{wm.filename}</p>
          <p className="text-muted-foreground">{wm.width}×{wm.height} · {local.position}</p>
        </div>
        <button data-testid={tid("remove")} type="button" disabled={off} onClick={remove} title={t("common.remove")} className="icon-action icon-action-danger">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {POSITIONS.map((p) => (
          <button key={p.v} type="button" data-testid={tid(`pos-${p.v}`)} disabled={off} onClick={() => set({ position: p.v })}
            className={"rounded-md border px-2 py-0.5 text-[11px] transition-colors disabled:opacity-40 " +
              (local.position === p.v ? "border-[#2E7BFF] bg-[#2E7BFF]/15 text-[#7FD4FF]" : "border-[#2E7BFF]/25 text-muted-foreground hover:text-foreground")}>
            {t(p.label)}
          </button>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <SliderRow label={t("watermark.size")} testid={tid("size")} value={local.size} min={3} max={60} disabled={off} onChange={(v) => set({ size: v })} />
        <SliderRow label={t("watermark.opacity")} testid={tid("opacity")} value={local.opacity} min={10} max={100} disabled={off} onChange={(v) => set({ opacity: v })} />
        <SliderRow label={t("watermark.margin")} testid={tid("margin")} value={local.margin} min={0} max={20} disabled={off} onChange={(v) => set({ margin: v })} />
      </div>
    </div>
  );
}

export function WatermarkPanel({ m, onUpdate, disabled }: PanelProps) {
  const { t } = useTranslation();
  const wms = m.watermarks ?? (m.watermark ? [m.watermark] : []);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      onUpdate(await addWatermark(m.id, f));
      toast.success(t("watermark.added", { n: wms.length + 1 }));
    } catch (err) {
      toast.error(errMsg(err, t("watermark.uploadFailed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="montage-watermark-panel" className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="font-display text-sm font-semibold uppercase tracking-wide">
          Watermark <span data-testid="montage-watermark-count" className="text-[#7FD4FF]">{wms.length > 0 ? `· ${wms.length}` : ""}</span>
        </p>
        <Button data-testid="montage-watermark-upload-btn" variant="secondary" size="sm" disabled={disabled || busy} onClick={() => fileRef.current?.click()}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {t("watermark.add")}
        </Button>
        <input ref={fileRef} data-testid="montage-watermark-input" type="file" className="hidden" accept="image/png,image/jpeg" onChange={onFile} />
      </div>
      {wms.length === 0 ? (
        <p data-testid="montage-watermark-empty" className="text-xs text-muted-foreground">
          {t("watermark.empty")}
        </p>
      ) : (
        <div className="space-y-3">
          {wms.map((wm, i) => (
            <WatermarkRow key={wm.id} m={m} wm={wm} index={i} onUpdate={onUpdate} disabled={disabled} />
          ))}
        </div>
      )}
    </Card>
  );
}

export function MusicPanel({ m, onUpdate, disabled }: PanelProps) {
  const { t } = useTranslation();
  const mu = m.music;
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState<MontageMusic | null>(mu);

  useEffect(() => { setLocal(mu); }, [mu?.updated_at, mu === null]);

  const commit = useDebounced<Partial<MontageMusic>>(async (patch) => {
    try {
      onUpdate(await patchMusic(m.id, patch as any));
    } catch (err) {
      toast.error(errMsg(err, t("music.notSaved")));
    }
  });

  const set = (patch: Partial<MontageMusic>) => {
    if (!local) return;
    setLocal({ ...local, ...patch });
    commit(patch);
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.(mp3|wav|m4a|aac|ogg)$/i.test(f.name)) {
      toast.error(t("music.badFormat"));
      return;
    }
    setBusy(true);
    try {
      onUpdate(await uploadMusic(m.id, f));
      toast.success(t("music.uploaded"));
    } catch (err) {
      toast.error(errMsg(err, t("music.uploadFailed")));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      onUpdate(await deleteMusic(m.id));
      toast.success(t("music.removed"));
    } catch (err) {
      toast.error(errMsg(err, t("watermark.removeFailed")));
    } finally {
      setBusy(false);
    }
  };

  const longer = !!local && local.duration > m.total_duration + 0.05;
  const shorter = !!local && local.duration < m.total_duration - 0.05;

  return (
    <Card data-testid="montage-music-panel" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-display text-sm font-semibold uppercase tracking-wide">{t("music.title")}</p>
        <div className="flex gap-2">
          <Button data-testid="montage-music-upload-btn" variant="secondary" size="sm"
                  disabled={disabled || busy} onClick={() => fileRef.current?.click()}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {mu ? t("music.replace") : t("music.upload")}
          </Button>
          {mu && (
            <Button data-testid="montage-music-remove-btn" variant="ghost" size="sm"
                    disabled={disabled || busy} onClick={remove}>
              <Trash2 className="h-3.5 w-3.5" /> Rimuovi
            </Button>
          )}
        </div>
        <input ref={fileRef} data-testid="montage-music-input" type="file" className="hidden"
               accept=".mp3,.wav,.m4a,.aac,.ogg,audio/*" onChange={onFile} />
      </div>

      {!local || !mu ? (
        <p data-testid="montage-music-empty" className="text-xs text-muted-foreground">
          {t("music.empty")}
        </p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <Music className="h-4 w-4 text-[#2E7BFF]" />
            <span data-testid="montage-music-filename" className="truncate text-foreground">{mu.filename}</span>
            <span data-testid="montage-music-duration">durata {fmtDuration(mu.duration)}</span>
          </div>
          <audio data-testid="montage-music-preview" controls preload="none"
                 src={musicAudioUrl(m.id, mu.updated_at)} className="w-full" />

          <div className="grid gap-4 sm:grid-cols-2">
            <SliderRow label={t("music.volume")} testid="montage-music-volume" value={local.volume} min={0} max={200}
                       disabled={disabled || busy} onChange={(v) => set({ volume: v })} />
            <SliderRow label={t("music.originalVolume")} testid="montage-original-volume" value={local.original_volume}
                       min={0} max={200} disabled={disabled || busy} onChange={(v) => set({ original_volume: v })} />
            <SliderRow label="Fade-in" testid="montage-music-fadein" value={local.fade_in} min={0} max={15} step={0.5}
                       suffix="s" disabled={disabled || busy} onChange={(v) => set({ fade_in: v })} />
            <SliderRow label="Fade-out" testid="montage-music-fadeout" value={local.fade_out} min={0} max={15} step={0.5}
                       suffix="s" disabled={disabled || busy} onChange={(v) => set({ fade_out: v })} />
          </div>

          <label className="flex items-center gap-3 text-xs text-muted-foreground">
            <input
              type="checkbox"
              data-testid="montage-music-loop"
              checked={local.loop}
              disabled={disabled || busy}
              onChange={(e) => set({ loop: e.target.checked })}
              className="h-4 w-4 accent-[#2E7BFF]"
            />
            <span>{t("music.loop")}</span>
          </label>

          <p data-testid="montage-music-hint" className="text-[11px] text-muted-foreground">
            {longer
              ? t("music.longer")
              : shorter
                ? local.loop
                  ? t("music.shorterLoop")
                  : t("music.shorterNoLoop")
                : t("music.fits")}
          </p>
        </div>
      )}
    </Card>
  );
}
